import { Card, Checkbox, Col, Divider, Flex, Row, Typography, Grid, Result } from 'antd';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import TextArea from 'antd/es/input/TextArea';

// components
import StatusComponent from 'components/status/status';
import ComponentHeader from 'components/componentHeader/componentHeader';
import DisabledElement from 'components/disabledElement/disabledElement';
import PageTitle from 'components/title/title';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import DoughnutComponent from 'modules/main/actions/assemblies/components/dougnet';

// services
import { onGetPublicAssemblySummary } from '../services/assemblySummary';
import useAblyChannel from 'core/hooks/useAblyChannel';
import { isRTL } from 'core/services/language';

// models
import { PublicAssemblySummary } from '../models/assemblySummary';
import { AssemblyVotingSummary } from 'modules/main/actions/assemblies/models/assemblies';
import { ASSEMBLY_SUMMARY_UPDATED, assemblyChannel, isValidVotingSummary } from 'modules/main/actions/assemblies/models/assemblyEvents';
import { StatusColors } from 'utils/statusColors';

const { useBreakpoint } = Grid;

interface PublicAssemblySummaryPageProps {}

const PublicAssemblySummaryPage: FunctionComponent<PublicAssemblySummaryPageProps> = () => {
	const { t } = useTranslation();
	const { request_number } = useParams<{ request_number: string }>();
	const screens = useBreakpoint();

	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);
	const [assembly, setAssembly] = useState<PublicAssemblySummary>();

	// read inside the realtime handler, which keeps the closure of its first render
	const isFetchingRef = useRef(false);

	useEffect(() => {
		getAssemblySummary();
	}, [request_number]);

	// the realtime payload has the same shape as the public summary, so it replaces it as is
	useAblyChannel<AssemblyVotingSummary>(assembly?.id ? assemblyChannel(assembly.id) : null, ASSEMBLY_SUMMARY_UPDATED, (summary) => {
		if (!isValidVotingSummary(summary)) {
			getAssemblySummary(true);
			return;
		}

		setAssembly((prev) => (prev ? { ...prev, ...summary } : prev));
	});

	const getAssemblySummary = async (silent = false) => {
		if (silent && isFetchingRef.current) return;

		try {
			isFetchingRef.current = true;
			if (!silent) setIsLoading(true);

			const response = await onGetPublicAssemblySummary(request_number!);

			setAssembly(response);
			setHasError(false);
		} catch (error) {
			// a background refresh must never replace the data already shown
			if (!silent) setHasError(true);
		} finally {
			isFetchingRef.current = false;
			if (!silent) setIsLoading(false);
		}
	};

	const isFinished = ['Completed', 'Approved', 'Published'].includes(assembly?.status!);

	return (
		<>
			<PageTitle name={t('TITLE.VOTING')} />

			{isLoading ? (
				<LoadingComponent />
			) : hasError || !assembly ? (
				<Card>
					<Result status="warning" title={t('ACTIONS.ERROR_FETCHING_ASSEMBLY')} />
				</Card>
			) : (
				<>
					<Card>
						<Flex align="center" gap={15} className="mb-4">
							<h5 style={{ fontWeight: 500, marginBottom: 0 }}>#{request_number}</h5>
							<StatusComponent
								text={isRTL() ? assembly.translated_status?.ar : assembly.translated_status?.en}
								color={StatusColors[assembly.status]}
								small
							/>
						</Flex>

						<Row gutter={[16, 16]}>
							<Col span={24} md={12}>
								<DisabledElement value={isRTL() ? assembly.translated_type.ar : assembly.translated_type.en} label={t('ACTIONS.TYPE_OF_ASSEMBLY')} />
								<DisabledElement
									value={assembly.assembly_quorum_percentage}
									label={t('ACTIONS.ATTENDANCE_QUORUM')}
									addonAfter={<span className="px-3 fw-semibold fw-se">%</span>}
								/>
								<DisabledElement value={assembly.date} label={t('ACTIONS.ASSEMBLY_DATE')} />
							</Col>

							<Col span={24} md={12}>
								<DoughnutComponent
									size={150}
									percentage={assembly.assembly_quorum_percentage_data?.percentage}
									data={[+assembly.assembly_quorum_percentage_data?.percentage, 100 - +assembly.assembly_quorum_percentage_data?.percentage]}
									colors={[assembly.assembly_quorum_percentage_data?.quorum_met ? '#78d12f' : '#e5252a', '#dedede']}
									header={
										<h6 className={`mb-0 fw-medium ${assembly.assembly_quorum_percentage_data?.quorum_met ? 'text-success' : 'text-danger'}`}>
											{assembly.assembly_quorum_percentage_data?.quorum_met ? t('ACTIONS.QUORUM_MET') : t('ACTIONS.QUORUM_NOT_MET')}
										</h6>
									}
									footer={
										<>
											<p className={`mb-0 fw-medium ${assembly.assembly_quorum_percentage_data?.quorum_met ? 'text-success' : 'text-danger'}`}>
												{t('ACTIONS.ATTENDEES')}: {assembly.assembly_quorum_percentage_data?.attendance_shareholders_count} /{' '}
												{assembly.assembly_quorum_percentage_data?.company_shareholders_count} {t('COMMON.SHAREHOLDERS')}
											</p>

											<p className={`mb-0 fw-medium ${assembly.assembly_quorum_percentage_data?.quorum_met ? 'text-success' : 'text-danger'}`}>
												{t('ACTIONS.OWNS')}: {assembly.assembly_quorum_percentage_data?.attendance_shareholders_shares} /{' '}
												{assembly.assembly_quorum_percentage_data?.total_company_shares} {t('COMMON.SHARES')}
											</p>
										</>
									}
								/>
							</Col>
						</Row>
					</Card>

					<Card className="mt-4">
						<ComponentHeader title={t('ACTIONS.DECISIONS')} subtitle={t('ACTIONS.ALL_DECISIONS_IN_ASSEMBLY')} />
						{assembly.decisions.map((decision, index) => (
							<Card key={decision.id ?? index} className="mt-4">
								<Row gutter={[16, 16]}>
									<Col span={24}>
										<Flex align="center" gap={10}>
											<h6 className="mb-0">
												{t('ACTIONS.DECISION')} ( {index + 1} )
											</h6>

											<StatusComponent
												small
												text={
													!assembly.assembly_quorum_percentage_data?.quorum_met
														? t('ACTIONS.ASSEMBLY_QUORUM_NOT_COMPLETE')
														: decision?.decision_quorum?.quorum_met
															? t('ACTIONS.QUORUM_MET')
															: t('ACTIONS.QUORUM_NOT_MET')
												}
												color={
													!assembly.assembly_quorum_percentage_data?.quorum_met ? 'red' : decision?.decision_quorum?.quorum_met ? 'green' : 'red'
												}
											/>
										</Flex>
									</Col>

									<Col span={24} lg={9}>
										<Typography style={{ fontSize: 15, marginBottom: 5 }}>{t('ACTIONS.DECISION_IN_AR')}</Typography>
										<TextArea disabled value={decision.translated_title.ar} autoSize={{ minRows: 4, maxRows: 4 }} />
									</Col>

									<Col span={24} lg={9}>
										<Typography style={{ fontSize: 15, marginBottom: 5 }}>{t('ACTIONS.DECISION_IN_EN')}</Typography>
										<TextArea disabled value={decision.translated_title.en} autoSize={{ minRows: 4, maxRows: 4 }} />
									</Col>

									<Col span={24} lg={6}>
										<Flex
											vertical={screens.lg}
											style={{
												flex: 1,
												flexDirection: screens.lg ? 'column' : 'row',
												alignItems: screens.lg ? 'flex-start' : 'center',
											}}
											gap={10}>
											<DisabledElement
												label={t('ACTIONS.QUORUM_PERCENTAGE')}
												value={decision.quorum_percentage}
												addonAfter={<span className="px-3 fw-semibold fw-se">%</span>}
											/>
											<Checkbox disabled checked={decision.type == 'ShareholderCount'} style={{ marginTop: screens.lg ? 0 : 15 }}>
												{t('ACTIONS.SHAREHOLDER_COUNT')}
											</Checkbox>
										</Flex>
									</Col>

									<Divider className="my-2" />

									<Col span={24} className="mt-4">
										<Row gutter={[16, 16]}>
											<Col span={24} lg={12}>
												<h6 style={{ textAlign: 'center', fontSize: '1.1rem' }}>{t('ACTIONS.CURRENT_VOTING_RESULT')}</h6>

												<Flex justify="space-around" align="center" gap={20} wrap style={{ marginBlock: 20, borderInlineEnd: '1px solid #ccc' }}>
													<DoughnutComponent
														percentage={decision.voting_result_details?.Yes}
														data={[+decision.voting_result_details?.Yes, 100 - +decision.voting_result_details?.Yes]}
														colors={['#78d12f', '#dedede']}
														footer={t('ACTIONS.YES')}
													/>

													<DoughnutComponent
														percentage={decision.voting_result_details?.No}
														data={[+decision.voting_result_details?.No, 100 - +decision.voting_result_details?.No]}
														colors={['#ee1e40', '#dedede']}
														footer={t('ACTIONS.NO')}
													/>

													<DoughnutComponent
														percentage={decision.voting_result_details?.Abstained}
														data={[+decision.voting_result_details?.Abstained, 100 - +decision.voting_result_details?.Abstained]}
														colors={['#2884EF', '#dedede']}
														footer={t('ACTIONS.ABSTAINED')}
													/>
												</Flex>

												{isFinished && (
													<Flex
														justify="center"
														align="center"
														gap={10}
														style={{ color: decision.voting_result_details?.WinningDecision ? 'var(--primary)' : 'red' }}>
														{decision.voting_result_details?.WinningDecision ? (
															<>
																{t('ACTIONS.FINAL_VOTING_RESULT')}:
																<span className="fw-medium">{t(`ACTIONS.${decision.voting_result_details.WinningDecision.toUpperCase()}`)}</span>
															</>
														) : (
															<span className="fw-medium">{t('ACTIONS.DECISION_FAILED')}</span>
														)}
													</Flex>
												)}
											</Col>

											<Col span={24} lg={12}>
												<h6 style={{ textAlign: 'center', fontSize: '1.1rem' }}>{t('ACTIONS.ATTENDANCE_VOTING_STATUS')}</h6>

												<Flex justify="space-around" align="center" gap={20} style={{ marginBlock: 20 }}>
													<DoughnutComponent
														percentage={decision.voting_result_details?.VoteParticipationPercentage}
														data={[
															+decision.voting_result_details?.VoteParticipationPercentage,
															100 - +decision.voting_result_details?.VoteParticipationPercentage,
														]}
														colors={['#78d12f', '#dedede']}
														footer={
															<div className="text-center">
																<p className="mb-1">{t('ACTIONS.VOTED_USERS')}</p>
																<p className={`mb-0 fw-medium text-success`}>
																	{t('ACTIONS.NUMBER_OF_SHAREHOLDER_ADDED_VOTES')}: {decision.voting_result_details?.VotedShareholdersCount}{' '}
																	{t('COMMON.SHAREHOLDERS')}
																</p>

																{decision.type == 'ShareCount' && (
																	<p className={`mb-0 fw-medium text-success`}>
																		{t('ACTIONS.OWNS')}: {decision.voting_result_details?.VotedShares} {t('COMMON.SHARES')}
																	</p>
																)}
															</div>
														}
													/>

													<DoughnutComponent
														percentage={decision.voting_result_details?.NoVotePercentage}
														data={[+decision.voting_result_details?.NoVotePercentage, 100 - +decision.voting_result_details?.NoVotePercentage]}
														colors={['#e5252a', '#dedede']}
														footer={
															<div className="text-center">
																<p className="mb-1">{t('ACTIONS.NO_VOTE')}</p>
																<p className={`mb-0 fw-medium text-danger`}>
																	{t('ACTIONS.NUMBER_OF_SHAREHOLDER_ADDED_NO_VOTES')}: {decision.voting_result_details?.NotVotedShareholdersCount}{' '}
																	{t('COMMON.SHAREHOLDERS')}
																</p>

																{decision.type == 'ShareCount' && (
																	<p className={`mb-0 fw-medium text-danger`}>
																		{t('ACTIONS.OWNS')}: {decision.voting_result_details?.NotVotedShares} {t('COMMON.SHARES')}
																	</p>
																)}
															</div>
														}
													/>
												</Flex>
											</Col>
										</Row>
									</Col>
								</Row>
							</Card>
						))}
					</Card>
				</>
			)}
		</>
	);
};

export default PublicAssemblySummaryPage;
